import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCandidateData } from "../../hooks/useCandidateData";
import { useUserResumeData } from "../../hooks/useUserResumeData";
import { formatDate } from "../../utils/dateUtils";
import ProfileCard from "../profile/ProfileCard";
import ResumeSection from "../resumes/ResumeSection";
import ResumeDetailModal from "../modals/ResumeDetailModal";
import InterviewDetailModal from "../modals/InterviewDetailModal";
import { theme } from "../../theme.js";
import api from "../../api/client";
import { useUser } from "../../contexts/userContext";
import { usePageLoading } from "../../contexts/LoadingContext";
import PageHeader, { PageCard, PageShell } from "../common/PageHeader";
import {
  Upload,
  Play,
  FileText,
  Sparkles,
  Mic,
  CheckCircle,
  AlertCircle
} from "lucide-react";

/* -----------------------------------------------------
   SUBCOMPONENT: Account Information Card
------------------------------------------------------ */
/* -----------------------------------------------------
   SUBCOMPONENT: Profile Completion Card
------------------------------------------------------ */
const ProfileCompletionCard = ({ completionRate, checklist, t }) => {
  return (
    <div className="w-full bg-white rounded-2xl p-6 border border-gray-100/80 shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
      <h3 className="text-base font-bold text-gray-900 mb-5">{t('myDetails.profileCompletion', 'Profile Completion')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
        {/* Left: Radial Progress */}
        <div className="md:col-span-3 flex flex-col items-center justify-center md:border-r md:border-gray-100 md:pr-4">
          <div className="relative w-24 h-24">
            <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
              <path
                className="text-gray-100"
                strokeWidth="3.5"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-[#6366f1]"
                strokeWidth="3.5"
                strokeDasharray={`${completionRate}, 100`}
                strokeLinecap="round"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-xl font-extrabold text-gray-900">{completionRate}%</span>
              <span className="text-[10px] text-gray-400 font-semibold mt-0.5">{t('myDetails.complete', 'Complete')}</span>
            </div>
          </div>
        </div>

        {/* Middle: Checklist (Two columns) */}
        <div className="md:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3.5 md:border-r md:border-gray-100 md:px-4">
          {checklist.map((item, index) => (
            <div key={index} className="flex items-center gap-2.5">
              {item.checked ? (
                <CheckCircle className="w-5 h-5 text-[#16a34a] flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 text-[#ea580c] flex-shrink-0" />
              )}
              <span className="text-xs font-semibold text-gray-650">{item.label}</span>
            </div>
          ))}
        </div>

        {/* Right: Graphic and visibility text */}
        <div className="md:col-span-3 flex flex-col items-center text-center md:pl-4 justify-center">
          {/* Clipboard illustration graphic */}
          <div className="w-20 h-20 flex items-center justify-center text-[#9f9dfa] mb-2">
            <svg className="w-full h-full" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Soft background blob/shape */}
              <path d="M15 50 C15 30, 30 18, 55 18 C80 18, 90 35, 90 55 C90 75, 70 85, 45 85 C20 85, 15 70, 15 50 Z" fill="#f5f6ff" />
              
              {/* Clipboard Body */}
              <rect x="28" y="20" width="40" height="54" rx="6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="white" />
              
              {/* Clipboard Clip */}
              <path d="M40 20V17C40 15.5 41.2 14.3 42.7 14.3H53.3C54.8 14.3 56 15.5 56 17V20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="white" />
              
              {/* Profile Icon inside Clipboard */}
              {/* User Head */}
              <circle cx="41" cy="35" r="4.5" stroke="currentColor" strokeWidth="2" />
              {/* User Shoulder */}
              <path d="M33 46C33 42.5 36 41 41 41C46 41 49 42.5 49 46" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              
              {/* Content Lines inside Clipboard */}
              {/* Short details next to profile */}
              <line x1="55" y1="33" x2="63" y2="33" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="55" y1="38" x2="60" y2="38" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              {/* Full width lines at the bottom */}
              <line x1="35" y1="52" x2="63" y2="52" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="35" y1="58" x2="58" y2="58" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <line x1="35" y1="64" x2="61" y2="64" stroke="#c7c5fd" strokeWidth="2" strokeLinecap="round" />

              {/* Pen/Pencil on the right */}
              <g transform="translate(68, 44) rotate(18)">
                {/* Pen body */}
                <rect x="-2" y="-14" width="4" height="20" rx="1" stroke="currentColor" strokeWidth="2" fill="white" />
                {/* Pen tip */}
                <path d="M-2 6L0 10L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="currentColor" />
                {/* Pen clip */}
                <line x1="1" y1="-8" x2="3" y2="-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </g>
            </svg>
          </div>
          <p className="text-xs text-gray-500 font-semibold leading-normal max-w-[170px]">
            {t('myDetails.visibilityText', 'Complete your profile to increase visibility to recruiters')}
          </p>
        </div>
      </div>
    </div>
  );
};

/* -----------------------------------------------------
   SUBCOMPONENT: Recent Activity Card
------------------------------------------------------ */
const RecentActivity = ({ activities, onActivityClick, t }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'resume_uploaded':
        return <Upload className="w-4 h-4" />;
      case 'resume_score':
        return <Sparkles className="w-4 h-4" />;
      case 'interview':
        return <Mic className="w-4 h-4" />;
      case 'practice_interview':
        return <Play className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="w-full bg-white rounded-2xl p-6 border border-gray-100/80 shadow-[0_2px_12px_rgba(0,0,0,0.02)]">
      <div className="flex items-center justify-between mb-4 border-b border-gray-50 pb-4">
        <h3 className="text-base font-bold text-gray-900">{t('myDetails.recentActivityTitle', 'Recent Activity')}</h3>
      </div>

      {activities.length === 0 ? (
        <div className="text-center py-6 text-gray-400 text-sm">
          {t('myDetails.noRecentActivity', 'No recent activity.')}
        </div>
      ) : (
        <div className="divide-y divide-gray-50">
          {activities.map((act) => {
            const isClickable = act.type === 'resume_uploaded' || act.type === 'interview';
            return (
              <div
                key={act.id}
                onClick={isClickable ? () => onActivityClick(act) : undefined}
                className={`flex items-center justify-between w-full py-3.5 rounded-xl px-2 -mx-2 text-left transition-colors ${
                  isClickable ? 'hover:bg-gray-50/50 cursor-pointer group' : 'cursor-default'
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`p-2 rounded-lg ${act.iconColor}`}>
                    {getIcon(act.iconType)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-gray-800">{act.title}</div>
                    <div className="text-xs text-gray-500 truncate mt-0.5 font-medium">{act.description}</div>
                  </div>
                </div>
                <div className={`flex items-center gap-1.5 text-gray-400 transition-colors ml-4 flex-shrink-0 ${isClickable ? 'group-hover:text-gray-600' : ''}`}>
                  <span className="text-[11px] font-medium">
                    {act.date ? new Date(act.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                    {act.date ? ` • ${new Date(act.date).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true })}` : ''}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CandidateDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { updateUser } = useUser();

  const {
    candidateData,
    updateCandidateData,
    loading: candidateLoading,
    error: candidateError,
    refetchData,
  } = useCandidateData();

  const {
    resumes,
    setResumes,
    loading: resumesLoading,
    error: resumesError,
    uploadLoading,
    deleteLoading,
    handleResumeUpload,
    handleResumeDelete,
    refreshResumes,
    setError: setResumesError,
    snackbar,
    setSnackbar,
  } = useUserResumeData();

  const [isEditing, setIsEditing] = useState(false);
  const [selectedResume, setSelectedResume] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    linkedin_url: "",
    github_url: "",
  });

  useEffect(() => {
    if (candidateData && !isEditing) {
      setEditForm({
        first_name: candidateData.first_name || "",
        last_name: candidateData.last_name || "",
        email: candidateData.email || "",
        mobile: candidateData.profile?.mobile && candidateData.profile.mobile !== "Not provided" ? candidateData.profile.mobile : "",
        linkedin_url: candidateData.profile?.linkedin_url || "",
        github_url: candidateData.profile?.github_url || "",
      });
    }
  }, [candidateData, isEditing]);

  const handleEdit = () => {
    if (candidateData) {
      setEditForm({
        first_name: candidateData.first_name || "",
        last_name: candidateData.last_name || "",
        email: candidateData.email || "",
        mobile: candidateData.profile?.mobile && candidateData.profile.mobile !== "Not provided" ? candidateData.profile.mobile : "",
        linkedin_url: candidateData.profile?.linkedin_url || "",
        github_url: candidateData.profile?.github_url || "",
      });
    }
    setIsEditing(true);
  };

  const normalizeUrl = (url, type) => {
    if (!url) return "";
    const trimmed = String(url).trim();
    if (!trimmed) return "";
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      return trimmed;
    }
    if (type === "linkedin") {
      return trimmed.includes("linkedin.com") ? `https://${trimmed}` : `https://www.linkedin.com/in/${trimmed}`;
    }
    if (type === "github") {
      return trimmed.includes("github.com") ? `https://${trimmed}` : `https://github.com/${trimmed}`;
    }
    return `https://${trimmed}`;
  };

  const handleSave = async () => {
    try {
      setUpdateLoading(true);
      const cleanLinkedin = normalizeUrl(editForm.linkedin_url, "linkedin");
      const cleanGithub = normalizeUrl(editForm.github_url, "github");

      const updatedData = {
        ...candidateData,
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        profile: {
          ...candidateData.profile,
          mobile: editForm.mobile,
          linkedin_url: cleanLinkedin,
          github_url: cleanGithub,
        },
      };
      await updateCandidateData(updatedData);
      setEditForm(prev => ({
        ...prev,
        linkedin_url: cleanLinkedin,
        github_url: cleanGithub,
      }));
      setIsEditing(false);
    } catch (error) {
      console.error("Save failed:", error);
      const data = error.response?.data;
      const errorMsg = data?.linkedin_url?.[0] || 
                       data?.github_url?.[0] || 
                       data?.mobile?.[0] || 
                       data?.error || 
                       data?.detail || 
                       "Failed to save profile.";
      alert(errorMsg);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleCancel = () => {
    if (candidateData) {
      setEditForm({
        first_name: candidateData.first_name || "",
        last_name: candidateData.last_name || "",
        email: candidateData.email || "",
        mobile: candidateData.profile?.mobile && candidateData.profile.mobile !== "Not provided" ? candidateData.profile.mobile : "",
        linkedin_url: candidateData.profile?.linkedin_url || "",
        github_url: candidateData.profile?.github_url || "",
      });
    }
    setIsEditing(false);
  };

  // Photo upload handler
  const handlePhotoUpload = async (file) => {
    try {
      setPhotoUploading(true);
      const formPayload = new FormData();
      formPayload.append('profile_picture', file);

      const firstName = (isEditing ? editForm.first_name : candidateData?.first_name) || "";
      const lastName = (isEditing ? editForm.last_name : candidateData?.last_name) || "";
      const mobile = (isEditing ? editForm.mobile : candidateData?.profile?.mobile) || "";
      const rawLinkedin = isEditing ? editForm.linkedin_url : candidateData?.profile?.linkedin_url;
      const rawGithub = isEditing ? editForm.github_url : candidateData?.profile?.github_url;
      const linkedin = normalizeUrl(rawLinkedin, "linkedin");
      const github = normalizeUrl(rawGithub, "github");

      if (firstName) formPayload.append('firstName', firstName);
      if (lastName) formPayload.append('lastName', lastName);
      if (mobile && mobile !== "Not provided") formPayload.append('mobile', mobile);
      if (linkedin) formPayload.append('linkedin_url', linkedin);
      if (github) formPayload.append('github_url', github);

      const response = await api.put('profile/', formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Refetch candidate data to get updated photo URL
      if (refetchData) await refetchData();

      // If user is editing, keep what they typed in editForm!
      if (isEditing) {
        setEditForm(prev => ({
          ...prev,
          first_name: firstName,
          last_name: lastName,
          mobile: mobile,
          linkedin_url: linkedin,
          github_url: github,
        }));
      }

      // Update localStorage and UserContext so the navbar avatar refreshes
      const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
      const newPhotoUrl = response?.data?.profile_picture || response?.data?.current_photo || null;
      if (newPhotoUrl) {
        currentUserData.user_profile_picture = newPhotoUrl;
        currentUserData.profile_picture = newPhotoUrl;
      }
      localStorage.setItem('user', JSON.stringify(currentUserData));
      updateUser(currentUserData);

      // Dispatch event for sidebar update
      const updatedEvent = new CustomEvent('profilePhotoUpdated', {
        detail: { user: currentUserData }
      });
      window.dispatchEvent(updatedEvent);
    } catch (err) {
      console.error('Photo upload failed:', err);
      const data = err.response?.data;
      const errorMsg = data?.linkedin_url?.[0] || 
                       data?.github_url?.[0] || 
                       data?.mobile?.[0] || 
                       data?.error || 
                       data?.detail || 
                       "Failed to upload photo.";
      alert(errorMsg);
    } finally {
      setPhotoUploading(false);
    }
  };

  // Photo remove handler
  const handlePhotoRemove = async () => {
    try {
      setPhotoUploading(true);
      const formPayload = new FormData();
      formPayload.append('remove_profile_picture', 'true');

      const firstName = (isEditing ? editForm.first_name : candidateData?.first_name) || "";
      const lastName = (isEditing ? editForm.last_name : candidateData?.last_name) || "";
      const mobile = (isEditing ? editForm.mobile : candidateData?.profile?.mobile) || "";
      const rawLinkedin = isEditing ? editForm.linkedin_url : candidateData?.profile?.linkedin_url;
      const rawGithub = isEditing ? editForm.github_url : candidateData?.profile?.github_url;
      const linkedin = normalizeUrl(rawLinkedin, "linkedin");
      const github = normalizeUrl(rawGithub, "github");

      if (firstName) formPayload.append('firstName', firstName);
      if (lastName) formPayload.append('lastName', lastName);
      if (mobile && mobile !== "Not provided") formPayload.append('mobile', mobile);
      if (linkedin) formPayload.append('linkedin_url', linkedin);
      if (github) formPayload.append('github_url', github);

      await api.put('profile/', formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Refetch candidate data to get updated state
      if (refetchData) await refetchData();

      if (isEditing) {
        setEditForm(prev => ({
          ...prev,
          first_name: firstName,
          last_name: lastName,
          mobile: mobile,
          linkedin_url: linkedin,
          github_url: github,
        }));
      }

      // Update localStorage and UserContext so the navbar avatar refreshes
      const currentUserData = JSON.parse(localStorage.getItem('user') || '{}');
      delete currentUserData.user_profile_picture;
      delete currentUserData.profile_picture;
      localStorage.setItem('user', JSON.stringify(currentUserData));
      updateUser(currentUserData);

      // Dispatch event for sidebar update
      const updatedEvent = new CustomEvent('profilePhotoUpdated', {
        detail: { user: currentUserData }
      });
      window.dispatchEvent(updatedEvent);
    } catch (err) {
      console.error('Photo remove failed:', err);
      alert('Failed to remove photo.');
    } finally {
      setPhotoUploading(false);
    }
  };

  // Dynamic Profile Subtitle (Degree • Top Skills)
  const profileSubtitle = useMemo(() => {
    const activeResume = resumes?.find(r => r.is_active) || resumes?.[0];
    if (activeResume?.parsed_data) {
      const edu = activeResume.parsed_data.education;
      const skills = activeResume.parsed_data.skills;
      
      let degree = "";
      if (Array.isArray(edu) && edu.length > 0) {
        const firstEdu = edu[0];
        const rawDegree = typeof firstEdu === 'string' ? firstEdu : (firstEdu.degree || firstEdu.education_details || "");
        if (rawDegree) {
          degree = rawDegree.length > 25 ? rawDegree.slice(0, 25) + "..." : rawDegree;
        }
      }
      
      let keySkills = "";
      if (Array.isArray(skills) && skills.length > 0) {
        keySkills = skills.slice(0, 2).map(s => s.length > 15 ? s.slice(0, 15) + "..." : s).join(", ");
      }
      
      if (degree && keySkills) {
        return `${degree} • ${keySkills}`;
      } else if (degree) {
        return degree;
      } else if (keySkills) {
        return keySkills;
      }
    }
    return "MCA Student • Java Developer";
  }, [resumes]);

  // Profile Completion checklist items
  const completionChecklist = useMemo(() => {
    const activeResume = resumes?.find(r => r.is_active) || resumes?.[0];
    const hasSocial = Boolean(
      candidateData?.profile?.linkedin_url || 
      candidateData?.profile?.github_url || 
      activeResume?.parsed_data?.linkedin || 
      activeResume?.parsed_data?.github || 
      activeResume?.parsed_data?.personalInfo?.linkedin || 
      activeResume?.parsed_data?.personalInfo?.github
    );

    return [
      { label: t('myDetails.checklist.fullName', 'Full Name'), checked: Boolean(candidateData?.first_name && candidateData?.last_name) },
      { label: t('myDetails.checklist.emailAddress', 'Email Address'), checked: Boolean(candidateData?.email) },
      { label: t('myDetails.checklist.phoneNumber', 'Phone Number'), checked: Boolean(candidateData?.profile?.mobile && candidateData?.profile?.mobile !== "Not provided" && candidateData?.profile?.mobile.trim() !== "") },
      { label: t('myDetails.checklist.profilePhoto', 'Profile Photo'), checked: Boolean(candidateData?.profile?.current_photo) },
      { label: t('myDetails.checklist.resumeUploaded', 'Resume Uploaded'), checked: Boolean(resumes && resumes.length > 0) },
      { label: t('myDetails.checklist.socialLinks', 'LinkedIn / GitHub'), checked: hasSocial }
    ];
  }, [candidateData, resumes, t]);

  // Profile Completion Percentage (rounded to nearest 10% for clean mockup state)
  const profileCompletion = useMemo(() => {
    const checkedCount = completionChecklist.filter(item => item.checked).length;
    return Math.round((checkedCount / 6) * 10) * 10;
  }, [completionChecklist]);

  // Chronologically sorted Recent Activity list (resumes + interviews)
  const recentActivities = useMemo(() => {
    const list = [];
    
    // Resumes uploaded
    if (resumes && resumes.length > 0) {
      resumes.forEach(r => {
        if (r.uploaded_at) {
          list.push({
            id: `resume-upload-${r.id}`,
            type: 'resume_uploaded',
            title: t('myDetails.activity.resumeUploaded', 'Resume uploaded'),
            description: r.name,
            timestamp: new Date(r.uploaded_at).getTime(),
            date: r.uploaded_at,
            originalItem: r,
            iconColor: 'bg-[#f0fdf4] text-[#16a34a]',
            iconType: 'resume_uploaded'
          });
          
          // Generate a resume analyzed activity if it has a score or is successfully uploaded
          const resumeScore = r.parsed_data?.score || r.score || 78;
          list.push({
            id: `resume-score-${r.id}`,
            type: 'resume_score',
            title: t('myDetails.activity.resumeAnalyzed', 'Resume analyzed'),
            description: t('myDetails.activity.scoreImprovedDesc', 'Your resume score improved to {{score}}%', { score: resumeScore }),
            timestamp: new Date(r.uploaded_at).getTime() + 10000, // slightly after upload
            date: new Date(new Date(r.uploaded_at).getTime() + 10000).toISOString(),
            originalItem: r,
            iconColor: 'bg-[#f5f3ff] text-[#8b5cf6]',
            iconType: 'resume_score'
          });
        }
      });
    }
    
    // Interviews completed or in progress
    if (candidateData?.interviews && candidateData.interviews.length > 0) {
      candidateData.interviews.forEach(i => {
        const dateStr = i.started_at || i.created_at;
        if (dateStr) {
          const isCompleted = i.status === 'completed';
          list.push({
            id: `interview-${i.id}`,
            type: 'interview',
            title: isCompleted ? t('myDetails.activity.interviewCompleted', 'Interview completed') : t('myDetails.activity.practiceInterview', 'Practice interview'),
            description: isCompleted 
              ? `AI Mock Interview - ${i.job_role || i.job_description_title || 'Java Developer'}` 
              : t('myDetails.activity.practiceDesc', 'Started a new AI Mock Interview'),
            timestamp: new Date(dateStr).getTime(),
            date: dateStr,
            originalItem: i,
            iconColor: isCompleted ? 'bg-[#eff6ff] text-[#3b82f6]' : 'bg-[#fff7ed] text-[#ea580c]',
            iconType: isCompleted ? 'interview' : 'practice_interview'
          });
        }
      });
    }
    
    return list.sort((a, b) => b.timestamp - a.timestamp).slice(0, 5);
  }, [resumes, candidateData?.interviews, t]);

  const handleActivityClick = (act) => {
    if (act.type === 'resume_uploaded' || act.type === 'resume_score') {
      setSelectedResume(act.originalItem);
      setShowResumeModal(true);
    } else if (act.type === 'interview') {
      setSelectedInterview(act.originalItem);
      setShowInterviewModal(true);
    } else if (act.type === 'application') {
      navigate('/my-applications');
    }
  };

  const isMainLoading = candidateLoading && !candidateData;
  usePageLoading(isMainLoading, { message: t('myDetails.loading', 'Loading…') });

  if (isMainLoading) return null;

  if (candidateError)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600 bg-[#fafafc]">
        {candidateError}
      </div>
    );

  if (!candidateData)
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 bg-[#fafafc]">
        {t('myDetails.noData')}
      </div>
    );

  return (
    <>
      <PageShell fill={false} className="bg-[#fafafc]" style={{ fontFamily: theme.fonts.body }}>
        {/* Hidden upload file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleResumeUpload}
          className="hidden"
          disabled={uploadLoading}
        />

        <PageCard overflow={false}>
            <PageHeader
              title={t('myDetails.pageTitle', 'My Details')}
              subtitle={t('myDetails.pageSubtitle', 'Manage your profile and personal information')}
            />
          <div className="p-3 sm:p-4">

          {/* Main Layout Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            
            {/* Left Column (Profile, Account Stats, Quick Actions, Recent Activity) */}
            <div className="lg:col-span-6 flex flex-col gap-6">
              <ProfileCard
                candidateData={candidateData}
                isEditing={isEditing}
                editForm={editForm}
                setEditForm={setEditForm}
                handleEdit={handleEdit}
                handleSave={handleSave}
                handleCancel={handleCancel}
                formatDate={formatDate}
                updateLoading={updateLoading}
                subtitle={profileSubtitle}
                onPhotoUpload={handlePhotoUpload}
                onPhotoRemove={handlePhotoRemove}
                photoUploading={photoUploading}
              />

              <ProfileCompletionCard
                completionRate={profileCompletion}
                checklist={completionChecklist}
                t={t}
              />

              <RecentActivity
                activities={recentActivities}
                onActivityClick={handleActivityClick}
                t={t}
              />
            </div>

            {/* Right Column (My Resumes Section) */}
            <div className="lg:col-span-6 lg:sticky lg:top-6">
              <ResumeSection
                resumes={resumes}
                setResumes={setResumes}
                loading={resumesLoading}
                error={resumesError}
                uploadLoading={uploadLoading}
                deleteLoading={deleteLoading}
                handleResumeUpload={handleResumeUpload}
                handleResumeDelete={handleResumeDelete}
                refreshResumes={refreshResumes}
                setError={setResumesError}
                snackbar={snackbar}
                setSnackbar={setSnackbar}
                onUploadClick={() => fileInputRef.current?.click()}
                handleResumeClick={(r) => {
                  setSelectedResume(r);
                  setShowResumeModal(true);
                }}
                formatDate={formatDate}
              />
            </div>

          </div>
        </div>
        </PageCard>

        <ResumeDetailModal
          resume={selectedResume}
          isOpen={showResumeModal}
          onClose={() => setShowResumeModal(false)}
        />

        <InterviewDetailModal
          interview={selectedInterview}
          isOpen={showInterviewModal}
          onClose={() => setShowInterviewModal(false)}
        />
      </PageShell>
    </>
  );
};

export default CandidateDetailPage;


